import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CoinsService } from '@services/coins/coins.service';
import { EventTransportService } from '@services/event-transport/event-transport.service';
import { LoggerService } from '@services/logger/logger.service';
import { NetworksService } from '@services/networks/networks.service';
import { parseToFirstNonZero } from '@services/utils';
import { ICurrency } from '@services/wallets/base-currency';
import { IParseService } from '@services/wallets/parser-tx/base-parse';
import { IOracleFee } from '@services/wallets/utils.service';
import { Wallet } from '@services/wallets/wallet.service';
import { ethers } from 'ethers';
import { Subscription } from 'rxjs';

import { CryptoWalletService } from '@services/crypto-wallet/crypto-wallet.service';
import { SelectedFee } from '../transaction-fee/transaction-fee.component';

@Component({
    selector: 'app-transaction-confirmation',
    templateUrl: './transaction-confirmation.component.html',
    styleUrls: [ './transaction-confirmation.component.scss' ],
})
export class TransactionConfirmationComponent implements OnInit
{
    public visibility: boolean = false;
    public processing: boolean = false;
    public showLoader: boolean = false;
    public feeAmount: string;
    public selectedFee: SelectedFee;
    public feeUnit: string;
    public tx;
    public currency: ICurrency;
    public etherscanLink: string;
    public allowReject = true;
    public feeAlert: boolean = false;
    public feeAlertShown: boolean = false;
    public showCustomGasPrice: boolean = false;
    public chainId: number;

    private wallet: Wallet;
    private eventSubscription: Subscription;
    private preparedTx;
    private parseServices: IParseService;
    private txInfo: { amount?: string; symbol?: string; icon?: string } = {};

    public feeOracle: IOracleFee;
    public baseBalance: string;
    public baseBalanceUSD: string;
    public feePrice: string = '0';
    public gasLimit: string;
    public feeLimit: string;
    public headerTitle: string = 'TRANSACTION-CONFIRMATION.TITLE';

    private readonly AUTOCONFIRM_ORIGINS = [
        'https://app.idoneus.io',
        'https://portfolio-stage.idoneus.io/'
    ];

    constructor(
        private eventTransport: EventTransportService,
        private coinsService: CoinsService,
        private network: NetworksService,
        private cdr: ChangeDetectorRef,
        private translateService: TranslateService,
        private logger: LoggerService,
        private cryptoWallet: CryptoWalletService
    ) { }

    async ngOnInit(): Promise<void>
    {
        this.eventSubscription = this.eventTransport
            .on('confirm-transaction')
            .subscribe(async (_data) =>
            {
                try
                {
                    console.log(_data);
                    this.processing = false;
                    this.etherscanLink = null;
                    this.tx = _data.data.tx;
                    this.wallet = _data.data.wallet;

                    const cryptoWallet =
                        await this.cryptoWallet.getCryptoWalletById(
                            this.wallet.getCryptoWalletId()
                        );

                    this.chainId = Number(_data.data.chainId);
                    this.preparedTx = { ...this.tx };

                    this.allowReject = _data.data.allow_reject ?? true;

                    this.show();
                    await this.init();
                    await this.setupTx();
                    this.showLoader = false;
                    this.cdr.detectChanges();

                    const referrer = document.referrer ? new URL(document.referrer).origin : null;
                    const isAutoConfirm = this.AUTOCONFIRM_ORIGINS.some((origin) => new URL(origin).origin === referrer);

                    if (_data.data.auto && !cryptoWallet.hw_type && isAutoConfirm)
                    {
                        await this.sendTransaction();
                    } else
                    {
                        window.parent.postMessage(
                            'hyperid-need-user-interaction',
                            '*'
                        );
                    }
                } catch (e)
                {
                    console.error(e);
                    this.transactionFailed(e.code, e.message);
                }
            });
    }

    private async init()
    {
        this.parseServices = await this.wallet.getParseServices();

        if (!this.tx.value)
        {
            this.tx.value = '0';
        }

        if (this.tx.chainId)
        {
            this.tx.chainId = Number(this.tx.chainId);
        }

        this.currency = await this.wallet.getBaseCoinByChainId(this.chainId);
        let txParsed = await this.parseServices.parseDataConfirmation(
            this.tx,
            this.chainId,
            this.currency
        );

        this.currency = await this.wallet.getInstanceTokenByAddress(
            txParsed.contract,
            this.chainId
        );

        if (!this.tx.gaxLimit && !this.tx.gas)
        {
            this.tx = await this.currency.populateTransaction(this.tx);
            this.preparedTx = { ...this.tx };
            txParsed = await this.parseServices.parseDataConfirmation(
                this.tx,
                this.chainId,
                this.currency
            );
            this.currency = await this.wallet.getInstanceTokenByAddress(
                txParsed.contract,
                this.chainId
            );
        }

        this.tx = txParsed;

        this.headerTitle = 'TRANSACTION-CONFIRMATION.TITLE';
        this.feeUnit = this.currency.getFeeUnit();
        this.gasLimit = this.tx.gasLimit ?? this.tx.gas;

        if (!this.gasLimit)
        {
            this.gasLimit = await this.currency.getFeeLimit(
                { to: this.tx.to, value: this.tx.amount.value },
                'string'
            );
        } else
        {
            this.feeLimit = Number(this.gasLimit).toString();
        }

        this.preparedTx.gasLimit = this.gasLimit;
        delete this.preparedTx.gas;

        await this.loadCurrencyAmount();
        this.feeOracle = await this.oracleConvertToCoin();
        await this.calculateValues();

        this.cdr.detectChanges();
    }

    public onFeeChange(_fee: SelectedFee)
    {
        this.feeAmount = _fee.amount;
        this.selectedFee = _fee;

        if (_fee.type !== 'fast')
        {
            this.feeAlert = true;
        }

        this.calculateFeeChange();
    }

    ngOnDestroy()
    {
        if (Boolean(this.eventSubscription))
        {
            this.eventSubscription.unsubscribe();
        }
    }

    public onBack()
    {
        this.transactionCanceled();
        this.hide();
    }

    public async sendTransaction()
    {
        this.processing = true;
        try
        {
            await this.calculateFeeChange();

            if (
                typeof this.preparedTx.chainId === 'string' &&
                ethers.isHexString(this.preparedTx.chainId)
            )
            {
                this.preparedTx.chainId = Number(
                    BigInt(this.preparedTx.chainId)
                );
            }
            let populatedTx = await this.currency.populateTransaction(
                { ...this.preparedTx },
                false
            );

            console.log(populatedTx);

            populatedTx = {
                ...populatedTx,
                gasPrice: this.preparedTx.gasPrice,
                maxFeePerGas: this.preparedTx.gasPrice,
            };

            const txInfo = {
                symbol: this.tx.symbol,
                value: this.tx.value,
                to: this.tx.to,
                icon: this.tx.icon,
            };

            const txResult = await this.currency.sendTransaction(
                populatedTx,
                txInfo
            );
            console.log(txResult);

            this.composeEtherscanLink(txResult.hash);

            if (!txResult.wait) return this.transactionFailed();

            await this.saveTxHistory(txResult);
            this.transactionSuccess(txResult.hash);
        } catch (error)
        {
            this.transactionFailed(error.code, error.message);
            console.error(error);
            this.logger.error(
                'Transaction confirmation component -- sendTransaction: ',
                error.toString(),
                ' -- error code: ',
                error.code
            );

            this.processing = false;
        }
    }

    public async saveTxHistory(txResult)
    {
        let amountToken = null;
        if (!!txResult.data && txResult.data !== '0x')
        {
            amountToken = (await this.currency.parseTransaction(txResult))
                .amount.value;
        }

        this.currency.saveTxHistory(
            txResult,
            amountToken || txResult.amount || '0'
        );
    }

    public hideFeeAlert()
    {
        this.feeAlert = false;
        this.feeAlertShown = true;
        this.cdr.detectChanges();
    }

    private async setupTx()
    {
        this.cdr.detectChanges();
        this.tx.symbol =
            this.tx.symbol ||
            this.txInfo.symbol ||
            this.currency.getSymbol() ||
            this.currency.getFeeSymbol() ||
            'ETH';
        this.tx.feeSymbol = this.currency.getFeeSymbol();
        this.tx.icon =
            this.txInfo.icon ||
            this.tx.icon ||
            (await this.coinsService.getSymbolIcon(null, this.tx.symbol));
        this.tx.value =
            this.tx.signature === 'approve'
                ? this.tx.amount.formatValue
                : parseToFirstNonZero(
                    this.tx.amount?.value ||
                    this.txInfo?.amount ||
                    this.tx.value
                );
        this.tx.price = this.tx.amount?.price;

        if (this.tx.signature === 'approve')
        {
            this.headerTitle = 'APPROVE-CONFIRMATION.TITLE';
        }

        this.checkPrice();
    }

    private async checkPrice(): Promise<void>
    {
        try
        {
            let price = (await this.currency.getPrice('usd')).price;

            this.tx.price = this.tx.amount.price = parseToFirstNonZero(
                Number(price) * Number(this.tx.value),
                2
            );
        } catch (e)
        {
            console.error('Not load price!');
            console.debug(e);
        }
    }

    private async calculateValues(): Promise<void>
    {
        if (!this.feeAmount || !this.tx.to)
        {
            return;
        }
        if (this.processing)
        {
            return;
        }

        this.processing = true;

        const preparedFee = this.currency.preparePrice(this.feeAmount);
        const preparedPriorityFee = this.currency.preparePrice(
            this.selectedFee.priorityAmount
        );
        this.preparedTx.gasPrice = preparedFee.price;
        this.preparedTx.maxPriorityFeePerGas = preparedPriorityFee.price;

        this.processing = false;
        this.cdr.detectChanges();
    }

    private async calculateFeeChange(): Promise<void>
    {
        if (!this.feeAmount || !this.tx.to)
        {
            return;
        }
        if (this.processing)
        {
            return;
        }

        this.processing = true;

        const preparedFee = this.currency.preparePrice(this.feeAmount);
        const preparedPriorityFee = this.currency.preparePrice(
            this.selectedFee.priorityAmount
        );
        this.preparedTx.gasPrice = preparedFee.price;
        this.preparedTx.maxPriorityFeePerGas = preparedPriorityFee.price;
        this.tx.fee = <any>(
            await this.parseServices.parseFeeData(
                { ...this.preparedTx, gasLimit: this.gasLimit },
                this.currency
            )
        );

        this.processing = false;
        this.cdr.detectChanges();
    }

    private transactionSuccess(_hash)
    {
        this.eventTransport.send('transaction-confirmation', {
            result: 'SUCCESS',
            hash: _hash,
        });
        this.eventTransport.send('toast', {
            message: 'Transaction sent!',
            type: 'success',
        });
        this.hide();
    }

    private transactionFailed(_errorCode?: string, _message?: string)
    {
        this.eventTransport.send('transaction-confirmation', {
            result: 'FAILED',
            code: _errorCode,
            message: _message,
        });

        this.eventTransport.send('toast', {
            message: 'Transaction failed',
            type: 'error',
        });
        this.hide();
    }

    private transactionCanceled()
    {
        this.eventTransport.send('transaction-confirmation', {
            result: 'CANCELED',
        });
        this.hide();
    }

    private show()
    {
        this.showLoader = true;
        this.visibility = true;
        this.cdr.detectChanges();
    }

    private hide()
    {
        this.feeAmount = null;
        this.visibility = false;
        this.cdr.detectChanges();
    }

    private async composeEtherscanLink(_hash: string): Promise<void>
    {
        this.etherscanLink = this.currency.getLinkToExplorer(_hash);
    }

    public async oracleConvertToCoin(): Promise<IOracleFee>
    {
        const feeOracle = await this.currency
            .getWallet()
            .getFeeOracle(this.currency.getChainId());

        if (!this.feeAmount)
        {
            this.feeAmount = feeOracle.propose.amount;
            this.selectedFee = {
                amount: this.feeAmount,
                priorityAmount: this.feeAmount,
                type: 'propose',
            };
        }

        for (const key in feeOracle)
        {
            feeOracle[ key ].coinAmount = await this.currency
                .getWallet()
                .convertFeeGwei(
                    feeOracle[ key ].amount,
                    this.gasLimit,
                    this.preparedTx.gasPrice,
                    true
                );
        }

        this.cdr.detectChanges();
        return feeOracle;
    }

    public viewCustomFee()
    {
        this.showCustomGasPrice = !this.showCustomGasPrice;

        if (!this.showCustomGasPrice)
        {
            this.calculateFeeChange();
        }

        this.cdr.detectChanges();
    }

    public changeFeeCoin(customFee: string)
    {
        this.feeAmount = this.currency
            .getWallet()
            .convertFeeCoinToGwei(customFee, this.gasLimit);
        this.showCustomGasPrice = !this.showCustomGasPrice;
        this.selectedFee = {
            type: 'custom',
            amount: this.feeAmount,
            priorityAmount: this.feeAmount,
        };
        this.calculateFeeChange();
    }

    private async loadCurrencyAmount(currency = this.currency, data = null)
    {
        let priceData = data?.price_data;
        if (!priceData)
        {
            priceData = await currency.getPrice('usd', true);
            this.feePrice = priceData.price;
        }

        this.baseBalance = parseToFirstNonZero(
            Number(await currency.getBaseCurrencyBalance())
        );
        this.baseBalanceUSD = parseToFirstNonZero(
            Number(priceData.price) * Number(this.baseBalance)
        );
    }
}
